import type { TFunction } from "i18next";
import type { ErrorCode, ErrorParams } from "../contract/errors";

/** The client-only code the fetcher raises when the request never reached the service. */
export const CLIENT_ERROR_CODES = ["NETWORK"] as const;

// The keys a failed job writes to jobs.error_i18n_key are not listed here.
// They belong to the worker, which owns them in src/jobs/errorKeys.ts, and
// src/i18n must not import src/jobs (tests/import-boundaries.test.ts). The
// catalogue carries them under errors:jobs.*, and scripts/check-i18n.ts reads
// WORKER_ERROR_I18N_KEYS straight from that file, so there is one list.
export type UiErrorCode = ErrorCode | (typeof CLIENT_ERROR_CODES)[number];

/**
 * Which named param of an error code is its count. i18next picks the plural
 * form (CODE_one / CODE_other) from `count` only, and the API keeps its
 * params named after what they mean, so the mapping lives here, next to the
 * catalogue, and nowhere in the server.
 */
export const ERROR_COUNT_PARAM: Readonly<Partial<Record<UiErrorCode, string>>> = Object.freeze({
  INVALID_PASSWORD: "attemptsLeft",
  TOO_MANY_ATTEMPTS: "retryAfterMinutes",
});

/**
 * The translated message for an API error: errors:CODE with its params, the
 * plural form when the code's count param is present, the base entry
 * otherwise. Every code has a base entry, so a response without params still
 * reads as a sentence, never as a key.
 */
export function errorMessage(
  t: TFunction,
  code: UiErrorCode,
  params: ErrorParams | undefined = undefined,
): string {
  const countParam = ERROR_COUNT_PARAM[code];
  const count = countParam ? params?.[countParam] : undefined;
  const values: Record<string, unknown> = { ...params };
  if (typeof count === "number") values.count = count;
  return t(`errors:${code}`, values);
}
